import Borders from '@dynatrace/strato-design-tokens/borders';
import Colors from '@dynatrace/strato-design-tokens/colors';
import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { dtVisualRefreshEnabled } from '../../core/components/app-root/AppRoot.sty.js';

// Base structural overrides — used by both inline and overlay resize containers.
export const vrResizeHandleContainerBaseOverrides = {
  borderRadius: 0,
  height: '100%',
  width: Spacings.Size8,
  left: 0,
  transition: 'none',
};

// Styles for the ::before pseudo-element that draws the 1px visual divider line.
export const vrResizeHandleContainerBeforeOverrides = {
  content: '""',
  position: 'absolute' as const,
  top: 0,
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  width: Borders.Width.Default,
  backgroundColor: Colors.Border.Neutral.Subdued,
  pointerEvents: 'none' as const,
};

// Hover/active color changes applied to the ::before line.
export const vrResizeHandleContainerInteractionOverrides = {
  [`.${dtVisualRefreshEnabled} &:hover::before`]: {
    backgroundColor: Colors.Border.Neutral.SubduedHover,
  },
  [`.${dtVisualRefreshEnabled} &:active::before`]: {
    backgroundColor: Colors.Border.Neutral.SubduedActive,
  },
};

// Complete overrides for INLINE resize containers (between flex siblings in SplitLayout / Panel).
// Negative marginInline collapses the container to 0px of flex space so adjacent panels connect
// seamlessly. The container still renders 8px wide (4px each side of the boundary) for interaction.
// z-index: 1 ensures the container sits above both neighbouring panels.
export const vrResizeHandleContainerOverrides = {
  [`.${dtVisualRefreshEnabled} &`]: {
    ...vrResizeHandleContainerBaseOverrides,
    backgroundColor: 'transparent',
    marginInline: `calc(${Spacings.Size8} / -2)`,
    zIndex: 1,
  },
  [`.${dtVisualRefreshEnabled} &::before`]:
    vrResizeHandleContainerBeforeOverrides,
  ...vrResizeHandleContainerInteractionOverrides,
};

// The separator is invisible under VR — the container's ::before line is the visual divider — but it
// still owns `role="separator"`, the focus and the drag gesture, so it keeps the container's box
// rather than collapsing to 0. A zero-width box is reported as hidden by hit-testing tools even
// though the :after pseudo-element covers the track, which hides the handle from assistive
// technology and from any caller that measures it.
export const vrResizeSeparatorOverrides = {
  [`.${dtVisualRefreshEnabled} &`]: {
    width: Spacings.Size8,
    height: '100%',
    borderRadius: 0,
    backgroundColor: 'transparent',
    cursor: 'ew-resize',
    // The shared focus ring would now have a box to draw on. Keyboard focus is indicated by the
    // container's ::before turning into the accent line instead.
    boxShadow: 'none',
  },
  // The base rules paint an accent bar on hover and grow the separator on focus. Under VR both
  // states are drawn on the container's ::before instead, so the separator stays transparent.
  [`.${dtVisualRefreshEnabled} &:hover, .${dtVisualRefreshEnabled} &:focus-visible, .${dtVisualRefreshEnabled} &:hover&:focus-visible`]:
    {
      width: Spacings.Size8,
      height: '100%',
      backgroundColor: 'transparent',
    },
};

// Applied to the ::before pseudo-element when the resize handle has keyboard focus.
export const vrResizeHandleFocusOverrides = {
  backgroundColor: Colors.Border.Neutral.Accent,
  width: Borders.Width.Emphasized,
};
